import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useQuery, useZero } from '@rocicorp/zero/react'
import { Shell } from '../components/Shell.tsx'
import { applyDuplicateWarnings } from '../lib/fine-import/duplicates.ts'
import { matchFineRows } from '../lib/fine-import/name-match.ts'
import {
  dateInputToUtcMs,
  DAY_MS,
  parseFineText,
  todayDateInput,
  validatePreviewRow,
} from '../lib/fine-import/parse.ts'
import type { FeeType, FineImportMember, FinePreviewRow } from '../lib/fine-import/types.ts'
import { mutators } from '../zero/mutators.ts'
import { queries } from '../zero/queries.ts'
import { Require } from './guards.tsx'

type ImportState = 'idle' | 'queued' | 'confirmed' | 'failed'

function structuralErrors(row: FinePreviewRow): string[] {
  return row.validationErrors.filter(
    (error) => error.startsWith('Missing ') || error === 'Name is required',
  )
}

function revalidate(row: FinePreviewRow): FinePreviewRow {
  return {
    ...row,
    validationErrors: [...structuralErrors(row), ...validatePreviewRow(row)],
  }
}

function FineImportInner() {
  const z = useZero()
  const navigate = useNavigate()
  const [members, membersResult] = useQuery(queries.importMemberCandidates())
  const [feeDate, setFeeDate] = useState(todayDateInput)
  const feeDateMs = dateInputToUtcMs(feeDate)
  const [existingFees, feesResult] = useQuery(
    feeDateMs == null
      ? false
      : queries.feesForImportDate({ start: feeDateMs, end: feeDateMs + DAY_MS }),
  )
  const [text, setText] = useState('')
  const [rows, setRows] = useState<FinePreviewRow[] | null>(null)
  const [error, setError] = useState('')
  const [importState, setImportState] = useState<ImportState>('idle')
  const [statusMessage, setStatusMessage] = useState('')

  const matchMembers = useMemo<readonly FineImportMember[]>(
    () =>
      members.map((member) => ({
        id: member.id,
        displayName: member.displayName,
        fullName: member.private?.fullName,
      })),
    [members],
  )

  useEffect(() => {
    if (importState === 'queued' || importState === 'confirmed') return
    setRows((current) =>
      current
        ? applyDuplicateWarnings(matchFineRows(current, matchMembers), existingFees ?? [])
        : current,
    )
  }, [existingFees, importState, matchMembers])

  const summary = useMemo(() => {
    const allRows = rows ?? []
    return {
      ready: allRows.filter(
        (row) =>
          !row.skipRow &&
          row.matchedUserId != null &&
          row.validationErrors.length === 0,
      ).length,
      skipped: allRows.filter((row) => row.skipRow).length,
      unmatched: allRows.filter(
        (row) => !row.skipRow && row.matchedUserId == null,
      ).length,
      invalid: allRows.filter(
        (row) => !row.skipRow && row.validationErrors.length > 0,
      ).length,
    }
  }, [rows])

  const dataComplete =
    membersResult.type === 'complete' && feesResult.type === 'complete'

  function refresh(nextRows: FinePreviewRow[]): FinePreviewRow[] {
    return applyDuplicateWarnings(nextRows, existingFees ?? [])
  }

  function parse() {
    setError('')
    setStatusMessage('')
    setImportState('idle')
    try {
      const parsed = parseFineText(text)
      setRows(refresh(matchFineRows(parsed, matchMembers)))
    } catch (caught) {
      setRows(null)
      setError(caught instanceof Error ? caught.message : 'Unable to parse fines')
    }
  }

  function updateRow(
    rowId: string,
    update: (row: FinePreviewRow) => FinePreviewRow,
  ) {
    setRows((current) => {
      if (!current) return current
      return refresh(current.map((row) => (row.id === rowId ? revalidate(update(row)) : row)))
    })
  }

  function selectMember(rowId: string, userId: string) {
    const member = matchMembers.find((candidate) => candidate.id === userId)
    updateRow(rowId, (row) => ({
      ...row,
      matchedUserId: member?.id ?? null,
      matchedUserName: member?.displayName ?? null,
      confidence: member ? 1 : 0,
      matchStatus: member ? 'matched' : 'unmatched',
      manuallyMatched: true,
      hasDuplicate: false,
      duplicateWarning: null,
      allowDuplicate: false,
    }))
  }

  function toggleSkip(rowId: string) {
    updateRow(rowId, (row) =>
      row.skipRow
        ? {
            ...row,
            skipRow: false,
            skipReason: null,
            allowDuplicate: row.hasDuplicate,
          }
        : {
            ...row,
            skipRow: true,
            skipReason: 'manual',
            allowDuplicate: false,
          },
    )
  }

  async function confirmImport() {
    if (!rows || feeDateMs == null) return
    const blocked = rows.some(
      (row) =>
        !row.skipRow &&
        (row.matchedUserId == null || row.validationErrors.length > 0),
    )
    const readyRows = rows.filter(
      (row) =>
        !row.skipRow &&
        row.matchedUserId != null &&
        row.amountCents != null &&
        row.validationErrors.length === 0,
    )
    if (blocked || readyRows.length === 0) {
      setError('Resolve or skip every unmatched and invalid row before importing')
      return
    }

    const snapshot = rows
    setError('')
    setImportState('queued')
    setStatusMessage(`Adding ${readyRows.length} fee${readyRows.length === 1 ? '' : 's'} locally…`)

    try {
      const mutation = z.mutate(
        mutators.fees.import({
          feeDate: feeDateMs,
          rows: readyRows.map((row) => ({
            id: crypto.randomUUID(),
            userId: row.matchedUserId!,
            amountCents: row.amountCents!,
            description: row.description.trim(),
            type: row.type,
            allowDuplicate: row.allowDuplicate,
          })),
        }),
      )
      const clientResult = await mutation.client
      if (clientResult.type === 'error') {
        setImportState('failed')
        setRows(snapshot)
        setError(clientResult.error.message)
        setStatusMessage('')
        return
      }

      setStatusMessage(
        `${readyRows.length} fee${readyRows.length === 1 ? '' : 's'} queued for synchronization`,
      )
      void mutation.server.then((serverResult) => {
        if (serverResult.type === 'success') {
          setImportState('confirmed')
          setStatusMessage(
            `${readyRows.length} fee${readyRows.length === 1 ? '' : 's'} imported`,
          )
        } else {
          setImportState('failed')
          setRows(snapshot)
          setError(serverResult.error.message)
          setStatusMessage('')
        }
      })
    } catch {
      setImportState('failed')
      setRows(snapshot)
      setError('Failed to import fines')
      setStatusMessage('')
    }
  }

  return (
    <Shell title="Import fines" wide onBack={() => navigate({ to: '/admin' })}>
      <div className="card form-stack">
        <h2>Paste fines</h2>
        <p className="muted import-help">
          One per line: <code>Name - description $amount</code>. Amount defaults to $2.
        </p>
        <label>
          Fee date
          <input
            type="date"
            value={feeDate}
            onChange={(event) => {
              setFeeDate(event.target.value)
              setImportState('idle')
              setStatusMessage('')
            }}
          />
        </label>
        <label>
          Fines
          <textarea
            className="import-textarea"
            value={text}
            onChange={(event) => setText(event.target.value)}
            placeholder={'Campbell - Late to training\nDoe, John - Registration $180'}
          />
        </label>
        <button className="btn" type="button" onClick={parse} disabled={!text.trim()}>
          Parse and preview
        </button>
      </div>

      {error ? <p className="error">{error}</p> : null}

      {rows ? (
        <>
          <div className="import-summary">
            <span>{summary.ready} ready</span>
            <span>{summary.skipped} skipped</span>
            <span>{summary.unmatched} unmatched</span>
            <span>{summary.invalid} invalid</span>
          </div>
          <p className="status-line">
            <span className={`dot ${dataComplete ? 'dot--on' : 'dot--off'}`} />
            {dataComplete
              ? 'Names and duplicate data are synchronized'
              : 'Showing cached data while synchronization completes'}
          </p>

          <div className="card import-table-wrap">
            <table className="import-table">
              <thead>
                <tr>
                  <th>Source</th>
                  <th>Amount</th>
                  <th>Description</th>
                  <th>Type</th>
                  <th>Member</th>
                  <th>Match</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr
                    key={row.id}
                    className={[
                      row.skipRow ? 'import-row--skipped' : '',
                      row.hasDuplicate ? 'import-row--duplicate' : '',
                      row.validationErrors.length > 0 ? 'import-row--invalid' : '',
                    ].filter(Boolean).join(' ')}
                  >
                    <td>
                      <strong>{row.sourceName || `Line ${row.lineNumber}`}</strong>
                      <small>Line {row.lineNumber}</small>
                      {row.duplicateWarning ? (
                        <small className="warning-text">⚠ {row.duplicateWarning}</small>
                      ) : null}
                      {row.validationErrors.map((validationError) => (
                        <small className="error-text" key={validationError}>
                          {validationError}
                        </small>
                      ))}
                    </td>
                    <td>
                      <input
                        aria-label={`Amount for line ${row.lineNumber}`}
                        type="number"
                        min="0.01"
                        step="0.01"
                        value={row.amountCents == null ? '' : (row.amountCents / 100).toFixed(2)}
                        disabled={row.skipRow}
                        onChange={(event) => {
                          const amount = Number(event.target.value)
                          updateRow(row.id, (current) => ({
                            ...current,
                            amountCents:
                              event.target.value === '' || !Number.isFinite(amount)
                                ? null
                                : Math.round(amount * 100),
                          }))
                        }}
                      />
                    </td>
                    <td>
                      <input
                        aria-label={`Description for line ${row.lineNumber}`}
                        value={row.description}
                        maxLength={500}
                        disabled={row.skipRow}
                        onChange={(event) =>
                          updateRow(row.id, (current) => ({
                            ...current,
                            description: event.target.value,
                            hasDuplicate: false,
                            duplicateWarning: null,
                            allowDuplicate: false,
                          }))
                        }
                      />
                    </td>
                    <td>
                      <select
                        aria-label={`Type for line ${row.lineNumber}`}
                        value={row.type}
                        disabled={row.skipRow}
                        onChange={(event) =>
                          updateRow(row.id, (current) => ({
                            ...current,
                            type: event.target.value as FeeType,
                          }))
                        }
                      >
                        <option value="Fine">Fine</option>
                        <option value="Registration">Registration</option>
                        <option value="Expense">Expense</option>
                      </select>
                    </td>
                    <td>
                      <select
                        aria-label={`Member for line ${row.lineNumber}`}
                        value={row.matchedUserId ?? ''}
                        disabled={row.skipRow}
                        onChange={(event) => selectMember(row.id, event.target.value)}
                      >
                        <option value="">Select member…</option>
                        {matchMembers.map((member) => (
                          <option key={member.id} value={member.id}>
                            {member.displayName}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="import-confidence">
                      <span className={`match-status match-status--${row.matchStatus}`}>
                        {row.matchStatus}
                      </span>
                      <small>{Math.round(row.confidence * 100)}%</small>
                    </td>
                    <td>
                      <button
                        className={`btn btn-sm ${row.skipRow ? 'btn-success' : 'btn-secondary'}`}
                        type="button"
                        onClick={() => toggleSkip(row.id)}
                      >
                        {row.skipRow ? 'Include' : 'Skip'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <button
            className="btn"
            type="button"
            disabled={
              importState === 'queued' ||
              importState === 'confirmed' ||
              summary.ready === 0 ||
              summary.unmatched > 0 ||
              summary.invalid > 0
            }
            onClick={confirmImport}
          >
            {importState === 'queued' ? 'Queued for synchronization…' : `Import ${summary.ready} fines`}
          </button>
          {statusMessage ? <p className="form-message">{statusMessage}</p> : null}
          {importState === 'confirmed' ? (
            <button className="btn btn-secondary" type="button" onClick={() => navigate({ to: '/admin' })}>
              Back to admin
            </button>
          ) : null}
        </>
      ) : null}
    </Shell>
  )
}

export function FineImportPage() {
  return (
    <Require roles={['admin']} showDenied>
      <FineImportInner />
    </Require>
  )
}
